import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useChineseLang } from '../../i18n/chinese'
import ZhWords, { ClickWordHandler } from 'components/write-speak/ZhText'
import { Discussion, DiscussionAnswer, DiscussionTargetKind } from './discussionApi'
import { PREVIEW_DESKTOP, PREVIEW_PHONE, previewAnswers, unreadLine } from './discussionLogic'
import { Avatar, LikeButton, TeacherTick, useIsPhone } from './DiscussionBits'
import './discussion.css'

interface DiscussionPreviewProps {
  discussion: Discussion | null
  loading: boolean
  /** Called once when the preview first shows, to load the discussion. */
  onShow: () => void
  /** Opens the panel, at this answer when there is one. */
  onOpen: (answerRef?: string | null) => void
  story: any
  activeIndex: string
  onWordTap: (answer: DiscussionAnswer) => ClickWordHandler
  /** The panel's like action (useDiscussionThreads), so a like here shows there too. */
  onToggleLike: (kind: DiscussionTargetKind, id: string, liked: boolean, likes: number) => void
}

/**
 * "How other learners answered" at the end of Writing & Speaking, once the learner has sent their own
 * answer: three answers on desktop (two on a phone), two lines each, and one button for the
 * rest. Replies happen in the panel, never on the page.
 */
const DiscussionPreview: React.FC<DiscussionPreviewProps> = ({
  discussion,
  loading,
  onShow,
  onOpen,
  story,
  activeIndex,
  onWordTap,
  onToggleLike,
}) => {
  const { t } = useTranslation('discussion')
  const zhLang = useChineseLang()
  const isPhone = useIsPhone()
  // Load once, when the preview first shows (the parent passes a new function on every render).
  const onShowRef = useRef(onShow)
  useEffect(() => {
    onShowRef.current()
  }, [])

  const shown = previewAnswers(discussion, isPhone ? PREVIEW_PHONE : PREVIEW_DESKTOP)
  const total = discussion?.total || 0
  const mine = discussion?.answers.find((a) => a.mine)
  const unread = discussion?.unread || []

  return (
    <div className='ws-others-card dc-root dc-preview'>
      <div className='ws-others-top'>
        <div className='ws-label'>{t('title')}</div>
        {total > 0 && <span className='ws-others-n'>{t('answerCount', { count: total })}</span>}
      </div>

      {unread.length > 0 && (
        <button type='button' className='dc-unread' onClick={() => onOpen(mine?.ref || unread[0].answerRef)}>
          <span className='dc-dot' aria-hidden='true' />
          {unreadLine(unread)}
        </button>
      )}

      {!discussion && loading && <p className='dc-muted'>{t('loading')}</p>}
      {discussion && total === 0 && <p className='dc-muted'>{t('noAnswers')}</p>}

      {shown.length > 0 && (
        <div className='dc-pv-list'>
          {shown.map((a) => {
            const count = a.replies.length
            return (
              <div
                key={a.ref}
                className='dc-pv'
                role='button'
                tabIndex={0}
                aria-label={t('preview.open', { name: a.name })}
                onClick={() => onOpen(a.ref)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onOpen(a.ref)
                  }
                }}
              >
                <div className='dc-who'>
                  <Avatar name={a.name} teacher={a.isTeacher} />
                  {a.name}
                  {a.isTeacher && <TeacherTick />}
                </div>
                <div className='dc-pv-text' lang={zhLang}>
                  {a.content?.length ? (
                    <ZhWords
                      words={a.content}
                      iterator={`DISC-P-${a.ref}-`}
                      story={story}
                      activeIndex={activeIndex}
                      onClickWord={onWordTap(a)}
                      fontSize='16px'
                    />
                  ) : (
                    a.text
                  )}
                </div>
                <div className='dc-acts'>
                  {/* The card opens the panel; the heart likes, without opening anything. */}
                  <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                    <LikeButton
                      likes={a.likes || 0}
                      liked={!!a.liked}
                      own={false}
                      what={t('list.theirAnswer', { name: a.name })}
                      onToggle={() => onToggleLike('answer', a.ref, !!a.liked, a.likes || 0)}
                    />
                  </span>
                  <span className='dc-act'>{count > 0 ? t('replyCount', { count }) : t('list.reply')}</span>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {total > 0 && (
        <button type='button' className='ws-more' onClick={() => onOpen(null)}>
          {isPhone ? t('preview.seeAll', { count: total }) : t('preview.seeAllWithReplies', { count: total })}
        </button>
      )}
    </div>
  )
}

export default DiscussionPreview
